import React, { useState, useEffect, useCallback, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import { useDispatch, useSelector } from "react-redux";
import theme from "../../theme";
import CommonInput from "../input";
import CommonButton from "../button";
import CommonFormDropDown from "../formdropdown";
import {
    updateVendorProfileAction,
    getVendorProfileAction,
} from "../../containers/vendorDashboard/action";
import { getCountriesAction } from "../../containers/login/action";
import CommonBanksSelect from "../commonBanksSelect";
import CommonBankAccountNumber from "../commonBankAccountNumber";

const useStyles = makeStyles(() => ({
    dialogPaper: {
        width: "90%",
        maxWidth: "600px",
        margin: "20px auto",
        "@media (max-width: 768px)": {
            width: "95%",
            maxWidth: "100%",
            margin: "10px",
        },
    },
    dialogTitle: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "15px 20px",
        "@media (max-width: 768px)": {
            padding: "12px 15px",
        },
        "& .title": {
            color: theme.colors.Violet,
            fontSize: "18px",
            fontFamily: theme.fontFamily.MARKPROBOLD,
            "@media (max-width: 768px)": {
                fontSize: "16px",
            },
        },
    },
    dialogContent: {
        padding: "15px 20px",
        maxHeight: "70vh",
        overflowY: "auto",
        "@media (max-width: 768px)": {
            padding: "12px 15px",
            maxHeight: "75vh",
        },
    },
    dialogActions: {
        padding: "12px 20px",
        borderTop: `1px solid ${theme.colors.Zumthor}`,
        "@media (max-width: 768px)": {
            padding: "10px 15px",
            flexDirection: "column",
            gap: "10px",
            "& button": {
                width: "100%",
            },
        },
    },
    sectionHeader: {
        color: theme.colors.Violet,
        fontSize: "15px",
        fontFamily: theme.fontFamily.MARKPROBOLD,
        marginTop: "15px",
        marginBottom: "12px",
        paddingBottom: "8px",
        borderBottom: `2px solid ${theme.colors.Zumthor}`,
        "@media (max-width: 768px)": {
            fontSize: "14px",
            marginTop: "12px",
            marginBottom: "10px",
        },
        "&:first-child": {
            marginTop: "0",
        },
    },
    formRow: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: "15px",
        marginBottom: "15px",
        "@media (max-width: 768px)": {
            gridTemplateColumns: "1fr",
            gap: "12px",
            marginBottom: "12px",
        },
    },
    bankItem: {
        padding: "12px",
        border: `1px solid ${theme.colors.Zumthor}`,
        borderRadius: "8px",
        marginBottom: "12px",
        backgroundColor: theme.colors.White,
        "@media (max-width: 768px)": {
            padding: "10px",
            marginBottom: "10px",
        },
    },
    errorContainer: {
        backgroundColor: theme.colors.Cinderella,
        color: theme.colors.Monza,
        padding: "15px",
        borderRadius: "10px",
        marginBottom: "20px",
        fontFamily: theme.fontFamily.MARKPRO,
    },
    successDialog: {
        "& .MuiDialog-paper": {
            minWidth: "300px",
            maxWidth: "400px",
            borderRadius: "15px",
            padding: "10px",
        },
    },
    successDialogContent: {
        textAlign: "center",
        padding: "30px 20px",
        "& .successIcon": {
            fontSize: "64px",
            color: theme.colors.MintGreen,
            marginBottom: "15px",
            fontWeight: "bold",
            lineHeight: "1",
        },
        "& .successTitle": {
            color: theme.colors.Violet,
            fontSize: "20px",
            fontFamily: theme.fontFamily.MARKPROBOLD,
            marginBottom: "10px",
        },
        "& .successMessage": {
            color: theme.colors.Violet,
            fontSize: "14px",
            fontFamily: theme.fontFamily.MARKPRO,
        },
    },
    imageUploadContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "10px",
    },
    imagePreview: {
        width: "100px",
        height: "100px",
        objectFit: "cover",
        borderRadius: "8px",
        border: `1px solid ${theme.colors.Zumthor}`,
    },
    imagePreviewContainer: {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        marginTop: "5px",
    },
    removeImageButton: {
        cursor: "pointer",
        color: theme.colors.Monza,
        fontSize: "12px",
        fontFamily: theme.fontFamily.MARKPRO,
        textDecoration: "underline",
    },
}));

function VendorProfileUpdateModal({ open, onClose, vendorProfile }) {
    const classes = useStyles();
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);

    const updateVendorProfileMethod = useCallback(
        (data) => dispatch(updateVendorProfileAction(data)),
        [dispatch]
    );

    const getVendorProfileMethod = useCallback(
        () => dispatch(getVendorProfileAction()),
        [dispatch]
    );

    const getCountriesMethod = useCallback(
        () => dispatch(getCountriesAction()),
        [dispatch]
    );

    const vendorDashboardReducer = useSelector((state) => state.vendorDashboardReducer);
    const loginReducer = useSelector((state) => state.loginReducer);
    const hasHandledSuccessRef = useRef(false);
    const hasSubmittedFormRef = useRef(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [imagePreview, setImagePreview] = useState("");

    const [formData, setFormData] = useState({
        // User fields
        firstName: "",
        lastName: "",
        gender: null,
        image: null, // Will store File object or null
        country: null,
        branch: "",
        // Vendor fields
        businessName: "",
        businessDescription: "",
        address: "",
        phoneNumber: "",
        startTime: "",
        endTime: "",
        // Bank fields
        banks: [],
    });

    useEffect(() => {
        if (open) {
            hasHandledSuccessRef.current = false;
            hasSubmittedFormRef.current = false;
            setShowSuccessDialog(false);
            if (!loginReducer?.countryOptions?.length) {
                getCountriesMethod();
            }
        }
    }, [open, loginReducer?.countryOptions?.length, getCountriesMethod]);

    const isFormDataInitializedRef = useRef(false);

    useEffect(() => {
        if (open && vendorProfile && loginReducer?.countryOptions?.length && !isFormDataInitializedRef.current) {
            const updates = {};

            // User fields
            if (vendorProfile.user.first_name) updates.firstName = vendorProfile.user.first_name;
            if (vendorProfile.user.last_name) updates.lastName = vendorProfile.user.last_name;
            if (vendorProfile?.user?.gender) {
                updates.gender = {
                    value: vendorProfile?.user?.gender,
                    label: vendorProfile?.user?.gender === "M" ? "Male" : vendorProfile?.user?.gender === "F" ? "Female" : "Other"
                };
            }
            if (vendorProfile?.user?.image) {
                // Keep existing image URL for preview, but don't set it in formData
                // formData.image will remain null for existing images (only new uploads set it)
                setImagePreview(vendorProfile.user.image);
            }
            if (vendorProfile?.user?.country) {
                const countryOption = loginReducer.countryOptions.find(c => c.id === vendorProfile?.user?.country);
                if (countryOption) {
                    updates.country = { value: countryOption.id, label: countryOption.name };
                }
            }
            if (vendorProfile.branch) updates.branch = vendorProfile.branch;

            // Vendor fields
            if (vendorProfile.vendor) {
                if (vendorProfile.vendor.business_name) updates.businessName = vendorProfile.vendor.business_name;
                if (vendorProfile.vendor.business_description) updates.businessDescription = vendorProfile.vendor.business_description;
                if (vendorProfile.vendor.address) updates.address = vendorProfile.vendor.address;
                if (vendorProfile.vendor.phone_number) updates.phoneNumber = vendorProfile.vendor.phone_number;
                if (vendorProfile.vendor.start_time) updates.startTime = vendorProfile.vendor.start_time;
                if (vendorProfile.vendor.end_time) updates.endTime = vendorProfile.vendor.end_time;
            }

            // Bank fields
            if (vendorProfile.vendor.bank && Array.isArray(vendorProfile.vendor.bank)) {
                updates.banks = vendorProfile.vendor.bank;

            }
            if (Object.keys(updates).length > 0) {
                setFormData((prev) => ({ ...prev, ...updates }));
                isFormDataInitializedRef.current = true;
            }
        }
    }, [open, vendorProfile, loginReducer?.countryOptions]);

    useEffect(() => {
        if (!open) {
            isFormDataInitializedRef.current = false;
            setImagePreview("");
            // Reset image file when modal closes
            setFormData((prev) => ({ ...prev, image: null }));
        }
    }, [open]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Store the File object in formData
            setFormData((prev) => ({ ...prev, image: file }));
            // Create preview using FileReader
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = function () {
                setImagePreview(reader.result);
            };
            reader.onerror = function (error) {
                console.log("Error reading file: ", error);
            };
        }
    };

    const handleRemoveImage = () => {
        setFormData((prev) => ({ ...prev, image: null }));
        // Reset preview to existing image if available, otherwise clear it
        if (vendorProfile?.user?.image) {
            setImagePreview(vendorProfile.user.image);
        } else {
            setImagePreview("");
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    useEffect(() => {
        if (
            vendorDashboardReducer?.updateVendorProfileSuccess &&
            !hasHandledSuccessRef.current &&
            open &&
            hasSubmittedFormRef.current
        ) {
            hasHandledSuccessRef.current = true;
            getVendorProfileMethod();
            setShowSuccessDialog(true);
        }
    }, [vendorDashboardReducer?.updateVendorProfileSuccess, getVendorProfileMethod, open]);

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        hasHandledSuccessRef.current = false;
        hasSubmittedFormRef.current = false;
        onClose();
    };

    const handleUpdateProfile = () => {
        hasSubmittedFormRef.current = true;
        hasHandledSuccessRef.current = false;

        const submitData = {
            first_name: formData.firstName,
            last_name: formData.lastName,
            gender: formData.gender?.value || formData.gender,
            country: formData.country?.value || formData.country,
            vendor: {
                id: vendorProfile?.vendor?.id,
                business_name: formData.businessName,
                business_description: formData.businessDescription || "",
                address: formData.address,
                phone_number: formData.phoneNumber,
                start_time: formData.startTime,
                end_time: formData.endTime,
            },
            bank: formData.banks.map(bank => ({
                id: bank.id,
                bank_name: bank.bank_name,
                account_number: bank.account_number,
                account_name: bank.account_name,
                code: bank.code,
                paystack_id: bank.paystack_id || 0,
                recipient_code: bank.recipient_code || "",
            })),
        };

        // Only include image field if it's a File object (new upload)
        // If formData.image is null, don't include it (keeps existing image)
        // If formData.image is a File object, include it for upload
        if (formData.image instanceof File) {
            submitData.image = formData.image;
        }
        // If formData.image is null, we don't include it in submitData
        // This allows the API to keep the existing image

        updateVendorProfileMethod(submitData);
    };

    const handleAddBank = () => {
        setFormData((prev) => ({
            ...prev,
            banks: [
                ...prev.banks,
                {
                    id: null,
                    bank_name: "",
                    account_number: "",
                    account_name: "",
                    code: "",
                    paystack_id: 0,
                    recipient_code: "",
                },
            ],
        }));
    };

    const handleRemoveBank = (index) => {
        setFormData((prev) => ({
            ...prev,
            banks: prev.banks.filter((_, i) => i !== index),
        }));
    };

    const handleBankChange = (index, field, value) => {
        setFormData((prev) => ({
            ...prev,
            banks: prev.banks.map((bank, i) => (i === index ? { ...bank, [field]: value } : bank)),
        }));
    };
    const banksUnfilled = (formData.banks.find(bank => !bank.account_name) !== undefined && formData.banks.length > 0);
        
    const countryOptions = loginReducer?.countryOptions?.map(c => ({ value: c.id, label: c.name })) || [];
    const genderOptions = [
        { value: "M", label: "Male" },
        { value: "F", label: "Female" }
    ];

    return (
        <>
            {/* Success Dialog */}
            <Dialog
                open={showSuccessDialog}
                onClose={handleSuccessDialogClose}
                classes={{ paper: classes.successDialog }}
                aria-labelledby="success-dialog-title"
            >
                <DialogContent className={classes.successDialogContent}>
                    <div className="successIcon">✓</div>
                    <div className="successTitle">Profile Updated Successfully!</div>
                    <div className="successMessage">
                        Your profile information has been updated successfully.
                    </div>
                </DialogContent>
                <DialogActions style={{ justifyContent: "center", padding: "15px 20px" }}>
                    <CommonButton
                        text="OK"
                        stylevariant="primary"
                        onClick={handleSuccessDialogClose}
                    />
                </DialogActions>
            </Dialog>

            {/* Update Profile Dialog */}
            <Dialog
                open={open && !showSuccessDialog}
                onClose={onClose}
                maxWidth={false}
                classes={{ paper: classes.dialogPaper }}
                aria-labelledby="vendor-profile-update-dialog"
            >
                <DialogTitle className={classes.dialogTitle} disableTypography>
                    <div className="title">Update Profile</div>
                    <IconButton aria-label="close" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {vendorDashboardReducer?.updateVendorProfileError?.length ? (
                        <div className={classes.errorContainer}>
                            {Array.isArray(vendorDashboardReducer.updateVendorProfileError)
                                ? vendorDashboardReducer.updateVendorProfileError.map((each, index) => (
                                    <div key={index}>{each}</div>
                                ))
                                : <div>{vendorDashboardReducer.updateVendorProfileError}</div>}
                        </div>
                    ) : null}

                    {/* User Details Section */}
                    <div className={classes.sectionHeader}>User Details</div>
                    <div className={classes.formRow}>
                        <CommonInput
                            label="First Name"
                            placeholder="Enter first name"
                            value={formData.firstName}
                            onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                        />
                        <CommonInput
                            label="Last Name"
                            placeholder="Enter last name"
                            value={formData.lastName}
                            onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                        />
                    </div>
                    <div className={classes.formRow}>
                        <CommonFormDropDown
                            label="Gender"
                            placeholder="Select Gender"
                            options={genderOptions}
                            value={formData.gender}
                            onChange={(val) => setFormData((prev) => ({ ...prev, gender: val }))}
                        />
                        <CommonFormDropDown
                            label="Country"
                            placeholder="Select Country"
                            options={countryOptions}
                            value={formData.country}
                            onChange={(val) => setFormData((prev) => ({ ...prev, country: val }))}
                        />
                    </div>
                    <div className={classes.formRow}>
                        <div>
                            <div style={{
                                fontSize: "13px",
                                color: theme.colors.Violet,
                                fontFamily: theme.fontFamily.MARKPRO,
                                marginBottom: "6px",
                            }}>
                                Branch (View Only)
                            </div>
                            <div style={{
                                fontSize: "15px",
                                color: theme.colors.Violet,
                                fontFamily: theme.fontFamily.MARKPROHEAVY,
                            }}>
                                {formData.branch || vendorProfile?.branch || "N/A"}
                            </div>
                        </div>
                        <div className={classes.imageUploadContainer}>
                            <div style={{
                                fontSize: "13px",
                                color: theme.colors.Violet,
                                fontFamily: theme.fontFamily.MARKPRO,
                                marginBottom: "6px",
                            }}>
                                Profile Image
                            </div>
                            <CommonButton
                                stylevariant="secondary2"
                                type="button"
                                text="Upload Image"
                                onClick={() => {
                                    fileInputRef.current?.click();
                                }}
                                icon={"/images/upload.png"}
                            />
                            <input
                                type="file"
                                ref={fileInputRef}
                                style={{ display: "none" }}
                                accept="image/*"
                                onChange={handleImageChange}
                                onClick={(event) => {
                                    event.target.value = "";
                                }}
                            />
                            {imagePreview && (
                                <div className={classes.imagePreviewContainer}>
                                    <img
                                        src={imagePreview}
                                        alt="Profile preview"
                                        className={classes.imagePreview}
                                    />
                                    <span
                                        className={classes.removeImageButton}
                                        onClick={handleRemoveImage}
                                    >
                                        Remove
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Vendor Details Section */}
                    <div className={classes.sectionHeader}>Vendor Details</div>
                    <CommonInput
                        label="Business Name"
                        placeholder="Enter business name"
                        value={formData.businessName}
                        onChange={(e) => setFormData((prev) => ({ ...prev, businessName: e.target.value }))}
                    />
                    <CommonInput
                        label="Business Description"
                        placeholder="Enter business description"
                        value={formData.businessDescription}
                        onChange={(e) => setFormData((prev) => ({ ...prev, businessDescription: e.target.value }))}
                        multiline
                        rows={3}
                    />
                    <CommonInput
                        label="Address"
                        placeholder="Enter address"
                        value={formData.address}
                        onChange={(e) => setFormData((prev) => ({ ...prev, address: e.target.value }))}
                    />
                    <div className={classes.formRow}>
                        <CommonInput
                            label="Phone Number"
                            placeholder="Enter phone number"
                            value={formData.phoneNumber}
                            onChange={(e) => setFormData((prev) => ({ ...prev, phoneNumber: e.target.value }))}
                        />
                        <div>
                            <div style={{
                                fontSize: "13px",
                                color: theme.colors.Violet,
                                fontFamily: theme.fontFamily.MARKPRO,
                                marginBottom: "6px",
                            }}>
                                Settlement Charge Percentage (View Only)
                            </div>
                            <div style={{
                                fontSize: "15px",
                                color: theme.colors.Violet,
                                fontFamily: theme.fontFamily.MARKPROHEAVY,
                            }}>
                                {vendorProfile?.vendor?.settlement_charge_percentage !== undefined
                                    ? `${vendorProfile.vendor.settlement_charge_percentage}%`
                                    : "N/A"}
                            </div>
                        </div>
                    </div>
                    <div className={classes.formRow}>
                        <CommonInput
                            label="Start Time"
                            type="time"
                            value={formData.startTime}
                            onChange={(e) => setFormData((prev) => ({ ...prev, startTime: e.target.value }))}
                        />
                        <CommonInput
                            label="End Time"
                            type="time"
                            value={formData.endTime}
                            onChange={(e) => setFormData((prev) => ({ ...prev, endTime: e.target.value }))}
                        />
                    </div>

                    {/* Bank Details Section */}
                    <div className={classes.sectionHeader}>Bank Details</div>
                    {formData.banks.map((bank, index) => (
                        <div key={index} className={classes.bankItem}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                <div style={{ fontFamily: theme.fontFamily.MARKPROBOLD, color: theme.colors.Violet, fontSize: "14px" }}>
                                    Bank {index + 1}
                                </div>
                                <CommonButton
                                    text="Remove"
                                    stylevariant="secondary2"
                                    onClick={() => handleRemoveBank(index)}
                                    disabled={formData.banks[index].id !== null}
                                    style={{ padding: "4px 12px", fontSize: "11px" }}
                                />
                            </div>
                            <div className={classes.formRow}>
                                <CommonBanksSelect
                                    value={{ label: bank?.bank_name, value: bank?.code }}
                                    onChange={(val) => {
                                        handleBankChange(index, "code", val?.value);
                                        handleBankChange(index, "bank_name", val?.label);
                                    }}
                                    fullWidth
                                    disabled={!!bank?.id}
                                />
                            </div>
                            <div className={classes.formRow}>
                                <CommonBankAccountNumber
                                    value={{ account_number: bank.account_number || "", bank_code: bank.code || "", account_name: bank.account_name || "" }}
                                    onChange={(val) => {
                                        handleBankChange(index, "account_number", val?.account_number);
                                        handleBankChange(index, "account_name", val?.account_name);
                                    }}
                                    allowChange={!!bank?.id}
                                />
                            </div>
                        </div>
                    ))}
                    {/* <CommonButton
                        text="Add Bank"
                        stylevariant="secondary1"
                        onClick={handleAddBank}
                        disabled={vendorDashboardReducer?.loading || formData.banks.length >= 1}
                        style={{ marginBottom: "15px", marginTop: "5px" }}
                    /> */}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <CommonButton
                        text="Cancel"
                        stylevariant="secondary2"
                        onClick={onClose}
                    />
                    <CommonButton
                        text="Update Profile"
                        stylevariant="primary"
                        onClick={handleUpdateProfile}
                        // disabled if any bank is missing account name, account number, or code    
                        disabled={vendorDashboardReducer?.loading || banksUnfilled}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
}

export default VendorProfileUpdateModal;